// Calendrier interne des événements (M21, lot L5-C1, H7-05) : grille du mois (lundi en premier) avec les
// événements de chaque jour, liste détaillée du mois (organisateur, lieu, budget prévu, état) et création par
// le service des événements. Les événements en préparation restent internes jusqu'à leur publication.
import { CATEGORIES_EVENEMENT, type ResumeEvenement } from '@cc/contrats';
import { Bouton, EtatVide, Lien, PuceEtat, Tableau, type ColonneTableau } from '@cc/ui';
import { ChevronLeft, ChevronRight, Plus } from 'lucide-react';
import { useState } from 'react';
import { useNavigate } from 'react-router';
import { EnTetePage } from '../../commun/EnTetePage';
import { EtatRequete } from '../../commun/EtatRequete';
import { dateHeure } from '../../commun/format';
import { useSession } from '../../session/FournisseurSession';
import { peut } from '../../session/permissions';
import { useEvenements } from './api';
import { FormulaireEvenement } from './FormulairesEvenements';
import { bornesMois, evenementsDuJour, grilleMois, libelleEvenement, moisDecale, montantFcfa, TON_EVENEMENT } from './logique';
import styles from '../achats/achats.module.css';
import calendrier from './evenements.module.css';

const JOURS = ['Lundi', 'Mardi', 'Mercredi', 'Jeudi', 'Vendredi', 'Samedi', 'Dimanche'];
const moisCourant = () => new Date(Date.now() + 3_600_000).toISOString().slice(0, 7);
const libelleMois = (mois: string) => new Date(`${mois}-15T12:00:00Z`).toLocaleDateString('fr-FR', { month: 'long', year: 'numeric', timeZone: 'UTC' });

export function PageEvenements() {
  const { contexte } = useSession();
  const [mois, setMois] = useState(moisCourant);
  const { du, au } = bornesMois(mois);
  const evenements = useEvenements(du, au);
  const naviguer = useNavigate();
  const [creation, setCreation] = useState(false);
  const liste = evenements.data?.elements ?? [];
  const colonnes: ColonneTableau<ResumeEvenement>[] = [
    { id: 'numero', entete: 'Événement', enTeteLigne: true, masquable: false, cellule: (e) => <Lien href={`/evenements/${e.id}`} className="cc-mono">{e.numero}</Lien>, valeurTri: (e) => e.numero },
    { id: 'intitule', entete: 'Intitulé', cellule: (e) => `${e.intitule}${e.fictif ? ' (fictif)' : ''}` },
    { id: 'categorie', entete: 'Catégorie', cellule: (e) => CATEGORIES_EVENEMENT[e.categorie] },
    { id: 'debut', entete: 'Début', cellule: (e) => dateHeure(e.debut), valeurTri: (e) => e.debut },
    { id: 'lieu', entete: 'Lieu', cellule: (e) => e.lieu },
    { id: 'organisateur', entete: 'Organisateur', cellule: (e) => e.organisateur },
    { id: 'budget', entete: 'Budget prévu', cellule: (e) => montantFcfa(e.budget_prevu, e.fictif) },
    { id: 'etat', entete: 'État', cellule: (e) => <PuceEtat ton={TON_EVENEMENT[e.etat]} libelle={libelleEvenement(e.etat)} /> },
  ];
  return (
    <>
      <EnTetePage
        etiquette="Événements"
        titre="Calendrier des événements"
        description="Événements de la collectivité et des organisateurs extérieurs : réservation d'équipement, autorisation d'occupation, budget et dépenses, publication par le secrétaire général, bilan."
        actions={peut(contexte, 'evenement', 'creer') ? <Bouton variante="principal" icone={Plus} onClick={() => setCreation(true)}>Nouvel événement</Bouton> : null}
      />
      <div className={styles.actes}>
        <Bouton variante="secondaire" icone={ChevronLeft} onClick={() => setMois((m) => moisDecale(m, -1))}>Mois précédent</Bouton>
        <h2 className={calendrier.mois} aria-live="polite">{libelleMois(mois)}</h2>
        <Bouton variante="secondaire" icone={ChevronRight} onClick={() => setMois((m) => moisDecale(m, 1))}>Mois suivant</Bouton>
      </div>
      <EtatRequete chargement={evenements.isPending} erreur={evenements.error} onReessayer={() => void evenements.refetch()} lot="L5-C1" fonction="Le calendrier des événements">
        <div className={calendrier.defilement}>
        <table className={calendrier.grille} aria-label={`Calendrier de ${libelleMois(mois)}`}>
          <thead><tr>{JOURS.map((j) => <th key={j} scope="col">{j}</th>)}</tr></thead>
          <tbody>
            {grilleMois(mois).map((semaine, i) => (
              <tr key={i}>
                {semaine.map((jour, k) => (
                  <td key={jour ?? `vide-${i}-${k}`} className={jour ? undefined : calendrier.vide}>
                    {jour ? <span className={calendrier.numero}>{Number(jour.slice(8))}</span> : null}
                    {jour ? evenementsDuJour(liste, jour).map((e) => <Lien key={e.id} href={`/evenements/${e.id}`} className={calendrier.evenement}>{e.intitule}</Lien>) : null}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
        </div>
        <section className={styles.bloc} aria-labelledby="titre-evenements-mois">
          <h2 id="titre-evenements-mois">Événements du mois</h2>
          {liste.length === 0 ? <EtatVide titre="Aucun événement ce mois-ci"><p>Aucun événement n&apos;est enregistré sur cette période.</p></EtatVide> : <Tableau legende="Événements du mois" colonnes={colonnes} lignes={liste} cleLigne={(e) => e.id} cleColonnes="evenements-mois" />}
        </section>
      </EtatRequete>
      {creation ? <FormulaireEvenement onFermer={() => setCreation(false)} onEnregistre={(id) => void naviguer(`/evenements/${id}`)} /> : null}
    </>
  );
}
